import React, { useContext } from "react";
import { useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { authorized, buildFeatureFlagChecker } from "common/authorization";
import { SUPPORTED_LANGUAGES } from "common/constants";
import { ExtendedApolloClient } from "../../apollo";
import { useGetGuestAccountDetailsQuery } from "../../generated/graphql";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { getBrowserLanguage } from "../../utils/browserLanguage";
import { accountSupportsElevationCertificates } from "../../utils/documentUploads";
import {
  SELECTED_LANGUAGE_KEY,
  updateSelectedLanguage,
} from "../../utils/i18n/utils";
import { setTitle } from "../../utils/title";
import { PublicUserContext } from "../Authentication/PublicUserContext";
import Icon, { ICON_COLORS, Icons } from "../Common/Icons";
import UnavailablePage from "../Guest/UnavailablePage";
import { LoadingContainer } from "./__styles__/GuestAuthContext";
import { AuthContext } from "./AuthContext";
import { CurrentAdmin } from "./types";

interface Props {
  children: React.ReactNode;
}
const GuestAuthContextProvider = ({ children }: Props) => {
  const { currentPublicUser } = useContext(PublicUserContext);
  const browserLanguage = getBrowserLanguage({ stripRegion: true });
  const { t, i18n } = useTranslation();
  const { getValue, setValue } = useLocalStorage({
    key: SELECTED_LANGUAGE_KEY,
  });
  const client = useApolloClient() as ExtendedApolloClient;

  const { data, loading, error } = useGetGuestAccountDetailsQuery({
    onCompleted: data => {
      const supportedLanguages =
        data.account?.publicPortal.supportedLanguages || [];

      const defaultLanguage = SUPPORTED_LANGUAGES.ENGLISH;
      const setLanguage = getValue();

      const selectedLanguage = setLanguage || browserLanguage;
      const nonDefaultLanguageSelected =
        selectedLanguage !== defaultLanguage &&
        supportedLanguages.includes(selectedLanguage as SUPPORTED_LANGUAGES);

      if (nonDefaultLanguageSelected) {
        void updateSelectedLanguage({
          language: selectedLanguage,
          updateStaticTranslations: i18n.changeLanguage,
          updateDynamicTranslations: client.updateLanguage,
          updateLocalStorage: setValue,
        });
      }
    },
  });

  if (loading) {
    setTitle(t("common-loading-state"));
    return (
      <LoadingContainer>
        <Icon icon={Icons.LOADING} color={ICON_COLORS.LIGHT_GREY} />
      </LoadingContainer>
    );
  }

  if (error || !data?.account) return null;

  const account = data.account;

  const supportsElevationCertificates =
    accountSupportsElevationCertificates(account);

  const featureFlags = account?.featureFlags || [];

  return account.publicPortal.enabled ? (
    <AuthContext.Provider
      value={{
        account,
        user: null,
        admin: null,
        publicUser: currentPublicUser ?? null,
        isGuest: true,
        setAdmin: (_admin: CurrentAdmin) => {},
        isFeatureEnabled: buildFeatureFlagChecker(featureFlags),
        authorized: authDetails =>
          authorized({
            entity: {
              role: { name: currentPublicUser ? "publicUser" : "guest" },
              canViewPersonalIdentifiableInformation: false,
            },
            ...authDetails,
          }),
        supportsElevationCertificates,
      }}
    >
      {children}
    </AuthContext.Provider>
  ) : (
    <UnavailablePage />
  );
};

export default GuestAuthContextProvider;
